/**
 * Generic shell session state.
 *
 * Tab management, page loading, mini-app width, chat transitions, patch
 * state, URL builders, pending-context state. Lives in `@sourcepilot/ui`
 * because every app under `frontend/apps/*` needs this same shell
 * machinery — none of these atoms are typed with PDM/RFLP domain models.
 *
 * App-specific session state (currentEntityAtom, currentActionAtom,
 * etc., which carry PDM-domain types) lives in
 * `frontend/apps/sourcepilot/state/rflp-session.ts`.
 */

import { useCallback, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAtom, atom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { sessionStorage } from '@sourcepilot/ui/framework/data/utils';
import type { Step } from '@sourcepilot/generated/chat/api/step';
import type { Thread } from '@sourcepilot/generated/chat/api/thread';
import {
  buildUrl,
  getRouteForModel,
  modelForPath,
} from '@sourcepilot/ui/framework/data/route-resolver';
import { getDisplayLabel } from '@sourcepilot/ui/framework/data/display-resolver';

// ───────────────────────────────────────────────────────────────────────────
// Patch state
// ───────────────────────────────────────────────────────────────────────────

export interface PatchState {
  _type: string;
  _has_sketch: boolean;
  ids: Set<string>;
}

export const emptyPatchState = {
  _type: '',
  _has_sketch: false,
  ids: new Set<string>(),
};

export const patchStateAtom = atomWithStorage<PatchState>(
  'patchState',
  emptyPatchState,
  sessionStorage,
);

// DEPRECATED: Old patch system - will be replaced by general data patching system
// TODO: Remove once new general data patching system is fully implemented
export const patchStepsAtom = atomWithStorage<Step[]>('patchSteps', [], sessionStorage);
export const patchThreadsAtom = atomWithStorage<Thread[]>('patchThreads', [], sessionStorage);

// ───────────────────────────────────────────────────────────────────────────
// Tab management
// ───────────────────────────────────────────────────────────────────────────

export interface TabData {
  id: string;
  title: string;
  contentType: string;
  viewed_at: Date;
  pathname: string;
  productSlug?: string;
  subpath?: string;
  taskClassName?: string;
}

export type NewTabData = Omit<TabData, 'pathname' | 'viewed_at'> & {
  _pathname?: string;
};

type TabController = {
  tabs: TabData[];
  activeTab: TabData | null;
  addTab: (newTab: NewTabData) => void;
  removeTab: (delTab: TabData) => void;
  removeTabs: (delTab: TabData[]) => void;
  reorderTabs: (startIndex: number, endIndex: number) => void;
  navigateToTab: (tab: NewTabData, extraPath?: string) => void;
  replaceActiveTab: (newTab: NewTabData) => void;
};

export const tabDataAtom = atomWithStorage<TabData[]>('tabs', [], sessionStorage);
export const isTransitioningFromNewAtom = atom(false);

export function useActiveTab(): TabData | null {
  const pathname = usePathname();
  const [tabs, setTabs] = useAtom(tabDataAtom);

  const currentPathname = decodeURIComponent(pathname || '');

  let bestMatch: TabData | null = null;
  let longestMatchLength = 0;

  for (const tab of tabs) {
    if (tab.pathname === '/') continue;
    if (currentPathname === tab.pathname || currentPathname.startsWith(tab.pathname + '/')) {
      if (tab.pathname.length > longestMatchLength) {
        bestMatch = tab;
        longestMatchLength = tab.pathname.length;
      }
    }
  }

  useEffect(() => {
    if (bestMatch) {
      setTabs((prevTabs) => {
        const tabIndex = prevTabs.findIndex(
          (t) => t.id === bestMatch!.id && t.contentType === bestMatch!.contentType,
        );
        if (tabIndex === -1) return prevTabs;

        const now = new Date();
        const timeSinceViewed = now.getTime() - prevTabs[tabIndex].viewed_at.getTime();
        if (timeSinceViewed < 500) return prevTabs;

        const newTabs = [...prevTabs];
        newTabs[tabIndex] = {
          ...newTabs[tabIndex],
          viewed_at: now,
        };
        return newTabs;
      });
    }
  }, [bestMatch?.id, bestMatch?.contentType, currentPathname, setTabs]);

  return bestMatch;
}

export const constructUrl = (tab: NewTabData): string => {
  const isListTab = tab.id?.startsWith('list:');
  let url = '';

  if (tab.productSlug && tab.id) {
    url = `/product/${tab.productSlug}/action/${tab.id}`;
  } else if (tab.id && !isListTab) {
    // `{ pure: true }` so buildUrl does NOT auto-append `?chat=/?agent=` —
    // constructUrl's output is used as `tab.pathname` for active-tab
    // MATCHING against the path-only `usePathname()`; trailing query would
    // break the match.
    const model = modelForPath(`/${tab.contentType}`);
    url =
      (model ? buildUrl(model, tab.id, undefined, { pure: true }) : null) ??
      `/${tab.contentType}/${tab.id}`;
  } else {
    url = `/${tab.contentType}`;
  }

  if (tab.subpath) {
    url += tab.subpath;
  }

  return url;
};

export const parseUrlToTab = (pathname: string): NewTabData | null => {
  if (!pathname || pathname === '/') return null;

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const [type, id, ...rest] = segments;

  if (type === 'product' && rest[0] === 'action') {
    const productSlug = id;
    const actionId = rest[1];
    const subpath = rest[2] ? `/${rest.slice(2).join('/')}` : undefined;

    return {
      id: actionId,
      title: '',
      contentType: 'action',
      productSlug,
      subpath,
      _pathname: `/product/${productSlug}/action/${actionId}`,
    };
  }

  if (rest.length >= 2) {
    const [relationship, childId, ...childRest] = rest;
    const childSubpath = childRest.length > 0 ? `/${childRest.join('/')}` : undefined;
    const childPathname = `/${type}/${id}/${relationship}/${childId}`;

    return {
      id: childId,
      title: '',
      contentType: relationship,
      subpath: childSubpath,
      _pathname: childPathname,
    };
  }

  const subpath = rest.length > 0 ? `/${rest.join('/')}` : undefined;

  // Canonicalise via the route-resolver so the scaffold path
  // (`/rflpprojects`) and a linkPath override (`/projects`) produce the
  // SAME contentType — without this, Shell seeding from a scaffold-path
  // landing collides with useRegisterEntityTab under the linkPath
  // contentType and you get two tabs for one entity.
  let contentType = type;
  let title = '';
  let canonicalPathname: string | undefined;
  const modelName = modelForPath(`/${type}`);
  if (modelName) {
    title = getDisplayLabel(modelName, !id);
    if (id) {
      const route = getRouteForModel(modelName);
      const seg = route.path.replace(/^\//, '').split('/')[0];
      if (seg) {
        contentType = seg;
        canonicalPathname = `${route.path}/${id}`;
      }
    }
  }

  return {
    id: id || `list:${type}`,
    title,
    contentType,
    subpath,
    _pathname: canonicalPathname,
  };
};

// ───────────────────────────────────────────────────────────────────────────
// Loading state
// ───────────────────────────────────────────────────────────────────────────

export interface LoadingState {
  type: 'none' | 'page' | 'subtab';
  path?: string;
  segment?: string;
}

export const loadingStateAtom = atom<LoadingState>({ type: 'none' });

export const pageLoadingAtom = atom(
  (get) => get(loadingStateAtom).type === 'page',
  (get, set, value: boolean) => {
    if (value) {
      set(loadingStateAtom, { type: 'page' });
    } else if (get(loadingStateAtom).type === 'page') {
      set(loadingStateAtom, { type: 'none' });
    }
  },
);

export const subtabLoadingAtom = atom(
  (get) => get(loadingStateAtom).type === 'subtab',
  (get, set, value: boolean) => {
    if (value) {
      set(loadingStateAtom, { type: 'subtab' });
    } else if (get(loadingStateAtom).type === 'subtab') {
      set(loadingStateAtom, { type: 'none' });
    }
  },
);

// ───────────────────────────────────────────────────────────────────────────
// useTabData
// ───────────────────────────────────────────────────────────────────────────

/** Project id from a `/projects/{id}/...` pathname, else null. Used to
 *  scope the side-panel chat param to a single project. */
const _projectIdOf = (pathname: string): string | null =>
  pathname.match(/^\/projects\/([^/]+)/)?.[1] ?? null;

export function useTabData(): TabController {
  const [tabs, setTabs] = useAtom(tabDataAtom);
  const [, setPageLoading] = useAtom(pageLoadingAtom);
  const router = useRouter();
  const activeTab = useActiveTab();

  const isSameTab = (newTab: NewTabData | TabData, tab: TabData | null) => {
    return tab?.id === newTab.id && tab?.contentType === newTab.contentType;
  };

  const replaceActiveTab = useCallback(
    (newTab: NewTabData) => {
      if (!activeTab) return;

      const baseTab = { ...newTab, subpath: undefined };
      const basePath = constructUrl(baseTab);

      const newTabWithPathname: TabData = {
        ...newTab,
        pathname: basePath,
        viewed_at: new Date(),
      };

      setTabs((prevTabs: TabData[]): TabData[] => {
        const index = prevTabs.findIndex((tab) => isSameTab(tab, activeTab));
        if (index === -1) return prevTabs;
        // The entity may already have a tab (the project page was open
        // when this chat attached to it). Two tabs with one identity
        // duplicate React keys and split the tab's state, so fold the
        // active tab INTO the existing one instead of adding a twin.
        const existingIndex = prevTabs.findIndex(
          (tab, i) => i !== index && isSameTab(newTabWithPathname, tab),
        );
        if (existingIndex !== -1) {
          return prevTabs
            .map((tab, i) =>
              i === existingIndex
                ? { ...tab, ...newTabWithPathname, viewed_at: new Date() }
                : tab,
            )
            .filter((_, i) => i !== index);
        }
        const updatedTabs = [...prevTabs];
        updatedTabs[index] = newTabWithPathname;
        return updatedTabs;
      });

      setPageLoading(true);
      let pathname = newTabWithPathname.pathname;
      if (newTab.subpath) {
        pathname += newTab.subpath;
      }

      const searchParams = new URLSearchParams();
      if (newTab.taskClassName && newTab.taskClassName !== 'ChatTask') {
        searchParams.set('agent', newTab.taskClassName);
      }
      const fullPath = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;

      router.push(fullPath, { scroll: false });
    },
    [activeTab, setTabs, setPageLoading, router],
  );

  const addTab = useCallback(
    (newTab: NewTabData): TabData => {
      const baseTab = { ...newTab, subpath: undefined };
      const basePath = newTab._pathname || constructUrl(baseTab);

      const newTabWithPathname: TabData = {
        ...newTab,
        pathname: basePath,
        viewed_at: new Date(),
      };

      let returnedTab: TabData = newTabWithPathname;

      setTabs((prevTabs: TabData[]): TabData[] => {
        const existingTab = prevTabs.find((tab) => isSameTab(newTab, tab));
        if (existingTab) {
          const subpathChanged = existingTab.subpath !== newTab.subpath;
          const titleChanged = newTab.title && existingTab.title !== newTab.title;
          // Route patterns can be renamed between sessions
          // (e.g. action-flow → action) — the stored tab still has the old
          // base path and active-tab matching breaks. Detect a fresh basePath
          // and refresh.
          const pathnameChanged = existingTab.pathname !== basePath;

          const mostRecentTab = [...prevTabs].sort(
            (a, b) => b.viewed_at.getTime() - a.viewed_at.getTime(),
          )[0];
          const isAlreadyMostRecent = isSameTab(existingTab, mostRecentTab);

          if (!subpathChanged && !titleChanged && !pathnameChanged && isAlreadyMostRecent) {
            returnedTab = existingTab;
            return prevTabs;
          }

          const updatedTab = {
            ...existingTab,
            viewed_at: new Date(),
            subpath: newTab.subpath,
            pathname: basePath,
            title: newTab.title || existingTab.title,
          };
          returnedTab = updatedTab;
          return prevTabs.map((tab) => (isSameTab(newTab, tab) ? updatedTab : tab));
        }

        return [...prevTabs, newTabWithPathname];
      });

      return returnedTab;
    },
    [setTabs],
  );

  const removeTab = useCallback(
    (delTab: TabData) => {
      const currentPathname = window.location.pathname;
      const isClosingActiveTab =
        currentPathname === delTab.pathname || currentPathname.startsWith(delTab.pathname + '/');

      let targetPathname: string | null = null;

      setTabs((prevTabs: TabData[]) => {
        const newTabs = prevTabs.filter((tab) => !isSameTab(tab, delTab));
        if (newTabs.length === 0) {
          targetPathname = '/';
          return [];
        }

        if (isClosingActiveTab) {
          const sortedTabs = [...newTabs].sort(
            (a, b) => b.viewed_at.getTime() - a.viewed_at.getTime(),
          );
          let targetTab = sortedTabs[0];

          if (targetTab && isSameTab(targetTab, delTab)) {
            targetTab = sortedTabs[1];
          }

          if (targetTab) {
            targetPathname = targetTab.pathname + (targetTab.subpath || '');
          }
        }

        return newTabs;
      });

      if (targetPathname) {
        setTimeout(() => {
          router.push(targetPathname!, { scroll: false });
        }, 0);
      }
    },
    [setTabs, router],
  );

  const removeTabs = useCallback(
    (delTab: TabData[]) => {
      setTabs((prevTabs: TabData[]): TabData[] => {
        const newTabs = prevTabs.filter((tab) => !delTab.some((del) => isSameTab(tab, del)));
        return newTabs;
      });
    },
    [setTabs],
  );

  const reorderTabs = useCallback(
    (startIndex: number, endIndex: number) => {
      setTabs((prevTabs: TabData[]): TabData[] => {
        const result = Array.from(prevTabs);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
      });
    },
    [setTabs],
  );

  const navigateToTab = useCallback(
    (tab: NewTabData, extraPathOrParams: string = '') => {
      const newTab = addTab(tab);

      setPageLoading(true);

      let pathname = newTab.pathname;
      if (tab.subpath) {
        pathname += tab.subpath;
      }

      let searchParams: URLSearchParams;
      if (extraPathOrParams.startsWith('?')) {
        searchParams = new URLSearchParams(extraPathOrParams.substring(1));
      } else if (extraPathOrParams.startsWith('/')) {
        pathname = pathname + extraPathOrParams;
        searchParams = new URLSearchParams(window.location.search);
      } else if (extraPathOrParams) {
        pathname = [pathname, extraPathOrParams].join('/');
        searchParams = new URLSearchParams(window.location.search);
      } else {
        // Compare against the active tab's id rather than path-segment-index,
        // which breaks on nested routes like `/projects/[pid]/action/[aid]`.
        // useActiveTab returns the longest-prefix match — correctly the
        // deepest entity.
        const isSameEntity = activeTab?.id === newTab.id;

        if (isSameEntity) {
          searchParams = new URLSearchParams(window.location.search);
        } else {
          // Different entity — clear page-local params (`mode`, `view`, …) but
          // KEEP the session-scoped ones (`chat`, `agent`) so a click between
          // tabs doesn't drop the user out of the chat they're in.
          //
          // Exception 1 — standalone chat dest (`/chat/{taskId}`): the task is
          // derived from the path and the agent from `tab.taskClassName`.
          // Carrying the previous `chat`/`agent` would override the path-
          // derived task (tab-content-area reads `?chat=` with priority over
          // the path), leaving the tab pointing at the new chat while the
          // content stays on the old one.
          //
          // Exception 2 — the side-panel chat is scoped to a PROJECT, not
          // global. Carry it only when staying within the SAME project (e.g.
          // drilling into a project's requirement / logical-part sub-pages).
          // Crossing to a DIFFERENT project — or entering a project from
          // elsewhere — drops it, so ProjectLayout's auto-open resolves that
          // project's own most-recently-attached chat. Non-project
          // destinations keep the carry (unchanged PDM / standalone flow).
          searchParams = new URLSearchParams();
          const destProject = _projectIdOf(pathname);
          const curProject = _projectIdOf(window.location.pathname);
          const carryChat =
            tab.contentType !== 'chat' &&
            (destProject === null ? true : destProject === curProject);
          if (carryChat) {
            const current = new URLSearchParams(window.location.search);
            for (const key of ['chat', 'agent'] as const) {
              const v = current.get(key);
              if (v) searchParams.set(key, v);
            }
          }
        }
      }

      if (tab.taskClassName && tab.taskClassName !== 'ChatTask') {
        searchParams.set('agent', tab.taskClassName);
      }

      const fullPath = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;

      router.push(fullPath, { scroll: false });

      return false;
    },
    [addTab, router, setPageLoading, activeTab],
  );

  return {
    tabs,
    addTab,
    removeTab,
    removeTabs,
    reorderTabs,
    navigateToTab,
    activeTab,
    replaceActiveTab,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Mini-app shell state
// ───────────────────────────────────────────────────────────────────────────

export const miniAppWidthAtom = atomWithStorage<number>('miniAppWidth', 240, sessionStorage);
// NOTE: chat width is NOT an atom — it's read synchronously from sessionStorage
// in a useState lazy initializer in TabContentArea, so the panel paints at the
// stored width on the very first render (an atomWithStorage, even with
// getOnInit, yields the default first and updates late, flashing the panel).
export const activeMiniAppAtom = atomWithStorage<string | null>(
  'activeMiniApp',
  null,
  sessionStorage,
);

// Graph expansion state — keyed by root node ID so each view preserves
// its own expanded/collapsed structure across navigations.
export interface GraphExpansionState {
  [rootNodeId: string]: {
    expandedNodeIds: string[];
    timestamp: number;
  };
}

export const graphExpansionStateAtom = atomWithStorage<GraphExpansionState>(
  'graphExpansionState',
  {},
  sessionStorage,
);

// ───────────────────────────────────────────────────────────────────────────
// Chat shell state
// ───────────────────────────────────────────────────────────────────────────

// 'shrink' = fullscreen to sidebar (width: 100% → chatWidth)
// 'slide'  = sidebar slide-in (translateX: 100% → 0)
// false    = no transition
export const chatTransitionAtom = atom<'shrink' | 'slide' | false>(false);

// Side-panel chat collapse state, keyed by chat/task id and persisted in
// sessionStorage (survives reload). The chat stays in the URL (`?chat=`)
// while collapsed — we only fade the panel down to a compact bar (project
// chat switcher + expand button). Removing the `?chat=` param can't be used
// to "close" the panel because ProjectChatAutoOpen would immediately re-add
// the most-recently-attached chat AND remounting ChatView would reload the
// thread context. Collapse is therefore a pure UI atom, per chat id so each
// chat remembers its own collapsed/expanded state.
export const chatCollapsedByIdAtom = atomWithStorage<Record<string, boolean>>(
  'chatCollapsedById',
  {},
  sessionStorage,
);

/**
 * Read/toggle the collapse state for a single chat id. Returns `false` for a
 * null/undefined id (no chat → nothing to collapse).
 */
export function useChatCollapsed(
  taskId: string | null | undefined,
): [boolean, (collapsed: boolean) => void] {
  const [map, setMap] = useAtom(chatCollapsedByIdAtom);
  const collapsed = taskId ? !!map[taskId] : false;
  const setCollapsed = useCallback(
    (value: boolean) => {
      if (!taskId) return;
      setMap((prev) => ({ ...prev, [taskId]: value }));
    },
    [taskId, setMap],
  );
  return [collapsed, setCollapsed];
}

// Pending chat messages — stores initial messages for newly created chats.
// User creates a chat from WorkspaceContent, the message is stored here, and
// ChatView picks it up once the thread is available from DAG subscription.
export interface PendingChatMessage {
  text: string;
  timestamp: number;
}

export const pendingChatMessagesAtom = atomWithStorage<Record<string, PendingChatMessage>>(
  'pendingChatMessages',
  {},
  sessionStorage,
);

// Pending context item — when user clicks "add to context" from a product
// interface, the item gets queued here for the current/next chat.
export interface PendingContextItem {
  id: string;
  name: string;
  search_path: string;
  _label: string;
}

export const pendingContextItemAtom = atom<PendingContextItem | null>(null);

// Task paused state — set by ChatView when task.status === 'paused'.
// Includes the task reference so deeply nested components can modify it.
export const taskPausedAtom = atom<{ isPaused: boolean; task: any | null }>({
  isPaused: false,
  task: null,
});
